<script setup lang="ts">
const { name: reporterName } = useReporter();
const { system: currentSystem } = useCurrentSystem();
const { search } = useWormholes();
const { user, loading: authLoading, isLoggedIn, fetchUser, login, logout } = useAuth();
const { portraitUrl } = useESI();
const toast = useToast();

const systemSearchTerm = ref('');
const systemSuggestions = computed(() => search(systemSearchTerm.value));

const route = useRoute();
const router = useRouter();
const navLinks = computed(() => [
  { label: 'Intel', to: '/', active: route.path === '/' || route.path.startsWith('/intel') },
  {
    label: 'Tools',
    to: '/tools',
    active: route.path.startsWith('/tools') || route.path === '/scout',
  },
  { label: 'Docs', to: 'https://eve-log-docs.pages.dev', target: '_blank' },
]);

// Loading timer — stops once auth completes (no global 1Hz polling)
const loadStart = Date.now();
const loadingSeconds = ref(0);
let loadingTimer: ReturnType<typeof setInterval> | null = setInterval(() => {
  loadingSeconds.value = Math.floor((Date.now() - loadStart) / 1000);
}, 1000);

watch(authLoading, (loading) => {
  if (!loading && loadingTimer) {
    clearInterval(loadingTimer);
    loadingTimer = null;
  }
});

onMounted(() => {
  fetchUser();

  // Show auth errors from callback redirect
  const authError = route.query.auth_error;
  if (authError) {
    toast.add({ title: 'Login failed', description: String(authError), color: 'error' });
    router.replace({ query: {} });
  }
});

// Always sync reporter name from EVE character
watch(user, (u) => {
  if (u) reporterName.value = u.characterName;
});
</script>

<template>
  <UApp>
    <UHeader title="">
      <template #left>
        <NuxtLink to="/">
          <UIcon name="i-lucide-radar" class="text-3xl" />
        </NuxtLink>
        <UNavigationMenu :items="navLinks" highlight class="hidden sm:flex" />
      </template>
      <template #right>
        <span class="text-xs text-(--ui-text-dimmed) hidden sm:inline"
          >v{{ $config.public.appVersion }}</span
        >
        <UInputMenu
          v-model="currentSystem"
          v-model:search-term="systemSearchTerm"
          :items="systemSuggestions"
          placeholder="Current system"
          size="sm"
          class="w-32 sm:w-40"
        />
        <UButton
          v-if="currentSystem"
          icon="i-lucide-x"
          variant="ghost"
          color="neutral"
          size="sm"
          class="-ml-1"
          @click="currentSystem = ''"
        />
        <template v-if="authLoading">
          <USkeleton class="h-8 w-8 rounded-full" />
        </template>
        <template v-else-if="isLoggedIn">
          <UButton
            :avatar="{ src: portraitUrl(user!.characterId, 32) }"
            :label="user!.characterName"
            variant="ghost"
            size="sm"
            class="hidden sm:flex"
          />
          <UButton
            label="Logout"
            variant="outline"
            size="sm"
            @click="logout"
            class="hidden sm:flex"
          />
        </template>
        <template v-else>
          <UButton label="Login with EVE" color="primary" size="sm" @click="login" />
        </template>

        <UColorModeButton />
      </template>
      <template #body>
        <UNavigationMenu :items="navLinks" orientation="vertical" class="sm:hidden" />
        <div v-if="isLoggedIn" class="flex items-center gap-2 mt-2 sm:hidden">
          <UButton
            :avatar="{ src: portraitUrl(user!.characterId, 32) }"
            :label="user!.characterName"
            variant="ghost"
            size="sm"
          />
          <UButton label="Logout" variant="outline" size="xs" @click="logout" />
        </div>
      </template>
    </UHeader>

    <UMain>
      <template v-if="authLoading">
        <div class="flex flex-col items-center justify-center py-20 gap-3">
          <UIcon name="i-lucide-rocket" class="text-4xl animate-spin text-(--ui-text-dimmed)" />
          <p class="text-sm">
            Stand by:
            <span v-if="loadingSeconds < 3" class="font-bold text-yellow-400">warming up</span>
            <span v-else class="font-bold text-red-400">overheating</span>
            <span class="text-(--ui-text-dimmed)"> ({{ loadingSeconds }}s)</span>
          </p>
          <pre class="text-xs text-(--ui-text-dimmed) leading-tight mt-2">
                                     __
         *                        _--//    __
                         *     _--  //  _--//
                            _-___  //_--  //         *
             .              /   ===//__   //
     .                      \     //  ===//
   *'                       /    //     //
  **               *      /    //     //        .
  **                     /    //     //
   **                   /    //     //
===__**               /     //     //
\    ===__     *     /     //     //      *
 ==__    ===__      /     //     //
     ===__    ===__/     //     //
       ** ===__         //_    //
         ** ***=       //  ==_//_             .
     .      ***//              =_ ===__
              || _   //         /__    ===__
               /  --//________/   ===__    ===__
     *        /    //  /   //          ===__==
             /    //  /   //                    .
            /    //  /   //    .
      *     \   //  /   //
             \ //   \  //            *
                     \//</pre
          >
        </div>
      </template>
      <template v-else-if="!isLoggedIn">
        <div class="flex flex-col items-center justify-center py-20 gap-4">
          <UIcon name="i-lucide-shield" class="text-6xl text-(--ui-text-dimmed)" />
          <p class="text-(--ui-text-muted)">
            Log in with your EVE character to access the scout tool.
          </p>
          <UButton label="Login with EVE" color="primary" size="lg" @click="login" />
        </div>
      </template>
      <template v-else>
        <NuxtPage />
      </template>
    </UMain>
  </UApp>
</template>
